import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { ALBUM_COVER, IMAGES, LINKS, scrollToId } from "@/data";
import {
  AppleMusicIcon,
  SpotifyIcon,
  YouTubeMusicIcon,
} from "@/components/PlatformIcons";

const HERO_PLATFORMS = [
  {
    name: "Spotify",
    href: LINKS.spotify,
    Icon: SpotifyIcon,
    primary: true,
    testid: "hero-spotify-cta",
  },
  {
    name: "Apple Music",
    href: LINKS.appleMusic,
    Icon: AppleMusicIcon,
    testid: "hero-apple-music-cta",
  },
  {
    name: "YouTube Music",
    href: LINKS.youtubeMusic,
    Icon: YouTubeMusicIcon,
    testid: "hero-youtube-music-cta",
  },
];

const LINES = [
  { text: "IL NUOVO EP", cls: "text-neutral-100" },
  { text: "È FUORI", cls: "text-outline-red" },
  { text: "ORA.", cls: "text-[#C8102E]" },
];

const lineVariants = {
  hidden: { y: "115%", rotate: 2 },
  visible: (i) => ({
    y: "0%",
    rotate: 0,
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.45 + i * 0.16 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const vinylY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={IMAGES.concert}
          alt=""
          className="h-[115%] w-full object-cover object-[50%_35%] opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/55 via-[#070707]/25 to-[#070707]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,rgba(200,16,46,0.28),transparent_55%)]" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-24 pt-36 sm:px-8 lg:grid-cols-[1.2fr_0.8fr] lg:pt-28"
      >
        <div>
          <motion.div
            data-testid="hero-release-badge"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mb-8 inline-flex items-center gap-3 border border-red-900/60 bg-black/50 px-4 py-2"
          >
            <span className="live-dot inline-flex h-2.5 w-2.5 rounded-full bg-red-600" />
            <span className="font-mono-tac text-[11px] uppercase tracking-[0.3em] text-red-500">
              Dal 6 Ottobre 2026 su tutte le piattaforme
            </span>
          </motion.div>

          <h1
            data-testid="hero-main-title"
            className="font-display text-[19vw] leading-[0.88] tracking-tight sm:text-8xl lg:text-[7.5rem]"
          >
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className={`block origin-left ${line.cls}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="font-lyric mt-7 max-w-md text-xl italic text-neutral-400 sm:text-2xl"
          >
            Rock italiano con anima grunge. Chitarre sporche, parole vere,
            visione lucida.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.35 }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            {HERO_PLATFORMS.map((p) => (
              <a
                key={p.name}
                data-testid={p.testid}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`group inline-flex items-center gap-3 px-6 py-4 font-mono-tac text-xs uppercase tracking-[0.2em] transition-all duration-200 hover:scale-105 active:scale-95 sm:text-sm ${
                  p.primary
                    ? "bg-[#C8102E] text-white hover:bg-[#FF2A4B] hover:shadow-[0_0_35px_rgba(200,16,46,0.6)]"
                    : "border border-neutral-700 bg-black/40 text-neutral-200 hover:border-red-600 hover:text-red-500"
                }`}
              >
                <p.Icon className="h-5 w-5" />
                Ascolta su {p.name}
              </a>
            ))}
            <button
              data-testid="hero-platforms-cta"
              onClick={() => scrollToId("album")}
              className="inline-flex items-center gap-2 px-3 py-4 font-mono-tac text-xs uppercase tracking-[0.2em] text-neutral-500 transition-colors duration-200 hover:text-red-500"
            >
              Tutte le piattaforme
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        </div>

        <motion.div
          style={{ y: vinylY }}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto hidden aspect-square w-full max-w-md sm:block lg:max-w-lg"
        >
          <div className="absolute inset-0 -z-10 scale-110 bg-[radial-gradient(circle,rgba(200,16,46,0.35),transparent_65%)] blur-2xl" />
          <div className="vinyl-spin relative h-full w-full overflow-hidden rounded-full border-2 border-red-950/70 shadow-[0_0_80px_rgba(200,16,46,0.35)]">
            <img
              src={ALBUM_COVER}
              alt="Copertina Linea Cremisi EP"
              data-testid="hero-vinyl-cover"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 rounded-full bg-[repeating-radial-gradient(circle_at_center,transparent_0px,transparent_3px,rgba(0,0,0,0.35)_4px)]" />
            <div className="absolute left-1/2 top-1/2 h-[22%] w-[22%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-red-900/60 bg-[#0D0D0D]">
              <div className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#C8102E]" />
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("album")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        aria-label="Scorri giù"
      >
        <motion.span
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block font-mono-tac text-[10px] uppercase tracking-[0.4em] text-neutral-500"
        >
          Scorri
        </motion.span>
      </motion.button>
    </section>
  );
}
